import { z } from "zod";

import { BillingTypeTermValidation, BookedGuestSurveyDisplayStatus, QuestionType } from "@/lib/gql/__generated__/graphql";
import {
	AdditionalFeeItemInputSchema,
	AdditionalFeeKidsPriceObjectInputSchema,
	BankInfoInputSchema,
	BillingTypeTermValidationSchema,
	HotelPolicyInputSchema,
	QuestionItemInputSchema,
} from "@/lib/zod/__generated__/validation-schema";

import {
	chillnnDateZodSchema,
	hotelInfoZodSchema,
	kidsPolicyZodSchema,
	nonEmptyStringZodSchema,
	priceObjectZodSchema,
	priceObjectZodSchemaFunc,
	requiredPrepMilliSecondBeforeDateZodSchema,
	requiredPrepMilliSecondBeforeHourZodSchema,
} from "../../../../../../_shared/validation-schema/validation-schema";

import type { ZodError } from "zod";

export const hotelPolicyInputZodSchema = HotelPolicyInputSchema.extend({
	hotelInfo: hotelInfoZodSchema,

	checkInStart: nonEmptyStringZodSchema,
	checkInEnd: nonEmptyStringZodSchema,
	checkOut: nonEmptyStringZodSchema,

	defaultInitPeopleNum: z.number().min(1).max(100),

	kidsPolicy: kidsPolicyZodSchema.nullish(),
	coSleepingKidsPrice: priceObjectZodSchema.nullish(),

	// 予約の締め切り
	requiredPrepMillisecondBeforeDate: requiredPrepMilliSecondBeforeDateZodSchema,
	requiredPrepMillisecondBeforeHour: requiredPrepMilliSecondBeforeHourZodSchema,

	// 事前質問
	questions: z.array(
		QuestionItemInputSchema.extend({
			question: z.string().min(1).max(8_000),
			options: z.array(z.string().min(1).max(500).or(z.literal(""))).nullish(),
			required: z.boolean().nullish(),
		}).refine(
			({ questionType, options }) => {
				if (
					questionType === QuestionType.Select ||
					questionType === QuestionType.Check ||
					questionType === QuestionType.Radio
				) {
					if (!options) {
						return false;
					}

					if (options.length < 1) {
						return false;
					}
				}

				return true;
			},
			{
				message: "選択肢を１つ以上作成してください",
				path: ["questionType"],
			},
		),
	),

	// 追加料金
	additionalFees: z.array(
		AdditionalFeeItemInputSchema.extend({
			name: z.string().min(1).max(100),
			price: priceObjectZodSchema,

			billingTypeTermValidation: BillingTypeTermValidationSchema.nullish(),
			termValidationApplyStartDt: chillnnDateZodSchema.nullish(),
			termValidationApplyEndDt: chillnnDateZodSchema.nullish(),

			kidsPrices: z
				.array(
					AdditionalFeeKidsPriceObjectInputSchema.extend({
						price: priceObjectZodSchemaFunc(100_000),
					}),
				)
				.nullish(),
		}).refine(({ billingTypeTermValidation, termValidationApplyStartDt, termValidationApplyEndDt }) => {
			if (billingTypeTermValidation === BillingTypeTermValidation.LimitedAnnually) {
				if (!!termValidationApplyStartDt && !!termValidationApplyEndDt) {
					return termValidationApplyStartDt < termValidationApplyEndDt;
				}
			}

			return true;
		}),
	),

	// キャンセル料金（現地決済）支払い期限
	localPaymentCancelFeeTransferLimitDays: z.object({
		limitDays: z.number().min(1).max(30),
	}),
	localPaymentCancelFeeTransferInfo: BankInfoInputSchema.extend({
		bankName: z.string().max(30), // 銀行名
		branchName: z.string().max(100), // 支店名
		bankAccountType: z.string(), // 口座種別
		bankAccountNumber: z.string().max(7), // 口座番号
		bankAccountHolderName: z.string().max(100), // 口座名義
	}).nullish(),

	// チェックイン・チェックアウト時間をゲスト表示/非表示の制御
	isShowCheckInOutTime: z.boolean(),

	// 予約完了画面の予約後アンケート表示。ホスト管理の 3 状態。
	bookedGuestSurveyDisplayStatus: z.nativeEnum(BookedGuestSurveyDisplayStatus),

	// 宿泊カレンダーの初期表示を空室のある直近の月にする
	isCalendarStartFromAvailableMonth: z.boolean(),

	// Input が任意なのは未送信時に現在値を維持するためで、このフォームは常に値を持つ
	isShowBestRateNotice: z.boolean(),
})
	.refine(({ checkInStart, checkInEnd }) => checkInStart <= checkInEnd, {
		message: "チェックイン終了時間にはチェックイン開始時間と同じか、それよりも後の時間帯を設定しましょう",
		path: ["checkInEnd"],
	})
	.superRefine(
		(
			{ isLocalPaymentAvailable, localPaymentCancelFeeTransferLimitDays, localPaymentCancelFeeTransferInfo },
			ctx,
		) => {
			if (isLocalPaymentAvailable) {
				const { limitDays } = localPaymentCancelFeeTransferLimitDays;

				// 現状 limitDays のエラーメッセージはジェネレータの挙動上 表示されない
				if (limitDays === null || limitDays === undefined) {
					ctx.addIssue({
						code: "custom",
						message: "支払い期限を入力してください",
						path: ["localPaymentCancelFeeTransferLimitDays", "limitDays"],
					});
				} else if (limitDays < 1) {
					ctx.addIssue({
						code: "custom",
						message: "支払い期限は1日以上で入力してください",
						path: ["localPaymentCancelFeeTransferLimitDays", "limitDays"],
					});
				}

				if (!localPaymentCancelFeeTransferInfo) {
					ctx.addIssue({
						code: "custom",
						message: "銀行名を入力してください",
						path: ["localPaymentCancelFeeTransferInfo", "bankName"],
					});
					return;
				}

				const { bankAccountHolderName, bankAccountNumber, bankAccountType, bankName, branchName } =
					localPaymentCancelFeeTransferInfo;

				if (!bankName) {
					ctx.addIssue({
						code: "custom",
						message: "銀行名を入力してください",
						path: ["localPaymentCancelFeeTransferInfo", "bankName"],
					});
				}

				if (!branchName) {
					ctx.addIssue({
						code: "custom",
						message: "支店名を入力してください",
						path: ["localPaymentCancelFeeTransferInfo", "branchName"],
					});
				}

				if (!bankAccountType) {
					ctx.addIssue({
						code: "custom",
						message: "口座種別を入力してください",
						path: ["localPaymentCancelFeeTransferInfo", "bankAccountType"],
					});
				}

				if (!bankAccountNumber) {
					ctx.addIssue({
						code: "custom",
						message: "口座番号を入力してください",
						path: ["localPaymentCancelFeeTransferInfo", "bankAccountNumber"],
					});
				}

				if (!bankAccountHolderName) {
					ctx.addIssue({
						code: "custom",
						message: "口座名義を入力してください",
						path: ["localPaymentCancelFeeTransferInfo", "bankAccountHolderName"],
					});
				}
			}
			return;
		},
	);

export type HotelPolicyZodSchemaType = z.infer<typeof hotelPolicyInputZodSchema>;

export type HotelPolicyZodParseErrorType = ZodError<HotelPolicyZodSchemaType>;
